"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AddressFieldLogic = void 0;
const isPlainObject_1 = __importDefault(require("lodash/isPlainObject"));
const omit_1 = __importDefault(require("lodash-es/omit"));
const pickBy_1 = __importDefault(require("lodash-es/pickBy"));
const union_1 = __importDefault(require("lodash-es/union"));
const react_fast_compare_1 = __importDefault(require("react-fast-compare"));
const clipboard_1 = require("../../clipboard");
const field_logic_1 = require("../../field-type-registry/field-logic");
const clipboard_2 = require("../../utils/clipboard");
const comparisons_1 = require("../../comparisons/comparisons");
const fields_1 = require("../../utils/fields");
const migration_1 = require("../../utils/migration");
const intl_1 = require("../../locale/intl");
const address_fieldtype_1 = require("../../safe-field-value/text-fields/address-field/address.fieldtype");
const texts_1 = require("../../texts");
const object_1 = require("../../utils/object");
const validation_1 = require("../../validation");
const fields_logic_registry_1 = require("../../field-type-registry/fields-logic-registry");
class AddressFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.name = "addressfield";
        this.label = (0, intl_1.$t)(texts_1.addressFieldText.address);
        this.icon = "address-field";
        this.iconColor = "#3EAC40";
        this.defaultMaxLength = 50;
        this.placeholder = (0, intl_1.$t)(texts_1.addressFieldText.type_an_address);
        this.searchSynonyms = [
            (0, intl_1.$t)(texts_1.addressFieldText.location),
            (0, intl_1.$t)(texts_1.addressFieldText.street),
            (0, intl_1.$t)(texts_1.addressFieldText.city),
            (0, intl_1.$t)(texts_1.addressFieldText.country),
            (0, intl_1.$t)(texts_1.addressFieldText.map),
        ];
        // @ts-expect-error: Need to resolve
        this.comparisons = {
            is: comparisons_1.is,
            is_not: comparisons_1.isNot,
            is_empty: comparisons_1.isEmpty,
            is_not_empty: comparisons_1.isNotEmpty,
            contains: comparisons_1.contains,
            not_contains: comparisons_1.notContains,
        };
        this.fieldValueGetter = new address_fieldtype_1.AddressFieldValueGetter();
        this.clipboardPasteRules = {
            textfield: clipboard_1.clipboardTextToAddress,
            recordtitlefield: clipboard_1.clipboardTextToAddress,
            textareafield: clipboard_1.clipboardTextToAddress,
            addressfield: clipboard_2.clipboardToSelf,
            unknownfield: clipboard_1.clipboardTextToAddress,
        };
    }
    get validators() {
        return { required: validation_1.requiredAddressField };
    }
    getEmptyValue() {
        return {
            location_address: "",
            location_address2: "",
            location_zip: "",
            location_country: "",
            location_state: "",
            location_city: "",
            location_longitude: "",
            location_latitude: "",
            sys_root: "",
        };
    }
    getInitialFieldValue(startValue) {
        return {
            location_address: startValue || "",
            location_address2: "",
            location_zip: "",
            location_country: "",
            location_state: "",
            location_city: "",
            location_longitude: "",
            location_latitude: "",
            sys_root: startValue || "",
        };
    }
    getInlineEditRules(field) {
        const { params } = field;
        const isSingle = params.display_format === "single";
        const baseInlineEditRules = super.getInlineEditRules(field);
        return {
            ...baseInlineEditRules,
            saveKeys: isSingle ? baseInlineEditRules.saveKeys : ["Enter"],
            deleteValueOnFirstKeypress: true,
            editorWidth: isSingle ? "100%" : "400px",
            hideError: !isSingle,
            allowAddRecordToGroup: true,
        };
    }
    prepareValueForClipboard(fieldValue) {
        var _a;
        return {
            text: (_a = fieldValue.sys_root) !== null && _a !== void 0 ? _a : "",
            rawValue: fieldValue,
        };
    }
    getInitialParams(_field, _structure) {
        return {
            display_fields: [
                "location_address",
                "location_address2",
                "location_city",
                "location_state",
                "location_zip",
                "location_country",
            ],
        };
    }
    processFieldValue(record, slug, comparison, field) {
        if (isAddressNestedField(slug)) {
            const nestedField = (0, fields_1.getAppFieldBySlug)([field], slug);
            return (0, fields_1.getValueFromRecordByField)(record, nestedField);
        }
        const value = super.processFieldValue(record, slug, comparison, field);
        return this.valueToString(value, field);
    }
    formatActivityRecordChangeValues(previousValue, currentValue, _extraData, field) {
        if (this.isValueEmpty(previousValue, field) ||
            this.isValueEmpty(currentValue, field)) {
            return { previousValue, currentValue };
        }
        const formattedPreviousValue = {};
        const formattedCurrentValue = {};
        const properties = (0, union_1.default)((0, object_1.getKeys)(previousValue), (0, object_1.getKeys)(currentValue));
        properties.forEach((property) => {
            if (property === "sys_root") {
                return;
            }
            if (previousValue[property] !== currentValue[property]) {
                // @ts-expect-error: Need to resolve
                formattedPreviousValue[property] = previousValue[property];
                // @ts-expect-error: Need to resolve
                formattedCurrentValue[property] = currentValue[property];
            }
        });
        return {
            previousValue: formattedPreviousValue,
            currentValue: formattedCurrentValue,
        };
    }
    isValueEqual(fieldValue1, fieldValue2) {
        // remove `sys_root` from comparison and falsy values
        // so we don't have false negatives when empty strings are compared to `null` or `undefined`.
        const value1 = (0, pickBy_1.default)((0, omit_1.default)(fieldValue1, ["sys_root"]), (value) => !!value);
        const value2 = (0, pickBy_1.default)((0, omit_1.default)(fieldValue2, ["sys_root"]), (value) => !!value);
        return (0, react_fast_compare_1.default)(value1, value2);
    }
    /*
     * Used for checking whether records can be moved between groups.
     * Records can be also grouped by Address nested fields(city, country, etc.).
     * In this case fromValue/toValue parameter will be equals to nested field value.
     * Moving records between nested field's groups is not allowed.
     */
    canChangeValue(field, fromValue, toValue) {
        if (isLocationFields(fromValue) || isLocationFields(toValue)) {
            return super.canChangeValue(field, fromValue, toValue);
        }
        return false;
    }
    valueToString(fieldValue, _field) {
        var _a;
        return (_a = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.sys_root) !== null && _a !== void 0 ? _a : "";
    }
    sortKey(fieldValue, field, userPreferences, record, structure, rule) {
        var _a, _b;
        const [, nestedSlug] = rule.field.split(".");
        if (nestedSlug) {
            return (0, fields_logic_registry_1.getFieldLogic)("textfield").sortKey(fieldValue[nestedSlug], field.params.nested[0], userPreferences, record, structure, rule);
        }
        return (_b = (_a = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.sys_root) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== null && _b !== void 0 ? _b : "";
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 170,
            minWidth: 90,
        };
    }
    migrateField(fieldTo, fieldFrom) {
        var _a;
        const displayFormats = ["single", "multiple"];
        const migratedField = super.migrateField(fieldTo, fieldFrom);
        return (0, migration_1.migrateDisplayFormat)(migratedField, displayFormats, (_a = fieldTo.params.display_format) !== null && _a !== void 0 ? _a : "");
    }
}
exports.AddressFieldLogic = AddressFieldLogic;
const isAddressNestedField = (slug) => {
    const [, childSlug] = slug.split(".");
    return !!childSlug && LocationFieldKeys.has(childSlug);
};
const LocationFieldKeys = new Set([
    "location_country",
    "location_country_code",
    "location_state",
    "location_city",
    "location_zip",
    "location_address",
    "location_address2",
    "location_point",
    "location_latitude",
    "location_longitude",
    "sys_root",
]);
function isLocationFields(value) {
    if (!(0, isPlainObject_1.default)(value)) {
        return false;
    }
    const keys = Object.keys(value);
    if (!keys.length) {
        return false;
    }
    return keys.every((key) => LocationFieldKeys.has(key));
}
